import classNames from 'classnames';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

// Styles
const s = require('./Pagination.css');

// Types
type PaginationPage = number | '<' | '>';

// Props
interface IPaginationProps {
    onPageClick: (page: number) => void;
    isLoading: boolean;
    totalEntries: number;
    entriesPerPage: number;
    showingFromOffset: number;
}

// Component
@observer
export class Pagination extends React.Component<IPaginationProps, {}> {
    static get defaultProps(): IPaginationProps {
        return {
            onPageClick: () => 1,
            isLoading: false,
            totalEntries: 0,
            entriesPerPage: 1,
            showingFromOffset: 0,
        };
    }

    @observable private changePage: string = '';

    @computed private get pagesCount(): number {
        return Math.ceil(this.props.totalEntries / this.props.entriesPerPage);
    }

    @computed private get allPages(): number[] {
        const pages: number[] = [];
        for (let page = 1; page <= this.pagesCount; page++) {
            pages.push(page);
        }
        return pages;
    }

    @computed private get currentPage(): number {
        let currentPage = 1;
        if (this.props.totalEntries) {
            this.allPages.some(page => {
                const rangeEnd = page * this.props.entriesPerPage;
                const rangeStart = rangeEnd - this.props.entriesPerPage;

                if (
                    this.props.showingFromOffset >= rangeStart &&
                    this.props.showingFromOffset < rangeEnd
                ) {
                    currentPage = page;
                    return true;
                }

                return false;
            });
        }

        return currentPage;
    }

    @computed private get pagination(): PaginationPage[] {
        if (this.allPages.length <= 1) {
            return [1];
        }

        const pagination: PaginationPage[] = [this.currentPage];

        let left = 7;
        while (left > 0) {
            const mod = left % 2;
            if (mod > 0) {
                if (pagination[pagination.length] >= this.pagesCount) {
                    pagination.unshift((pagination[0] as number) - 1);
                } else if (
                    pagination[pagination.length - 1] < this.pagesCount
                ) {
                    pagination.push(
                        (pagination[pagination.length - 1] as number) + 1
                    );
                }
            } else {
                if (pagination[0] > 1) {
                    pagination.unshift((pagination[0] as number) - 1);
                } else if (
                    pagination[pagination.length - 1] < this.pagesCount
                ) {
                    pagination.push(
                        (pagination[pagination.length - 1] as number) + 1
                    );
                }
            }

            left--;
        }

        if (pagination[0] > 1) {
            pagination.unshift('<');
            pagination.unshift(1);
        }

        if (pagination[pagination.length - 1] < this.pagesCount) {
            pagination.push('>');
            pagination.push(this.pagesCount);
        }

        return pagination;
    }

    public render() {
        if (this.props.isLoading && this.props.totalEntries <= 0) {
            return null;
        }

        return (
            <div className={s.pagination}>
                <form
                    onSubmit={this.handleGoToPage}
                    className={classNames(s.goto, {
                        [s.hidden]: this.pagination.length <= 1,
                    })}
                >
                    <p>Change page:</p>

                    <input
                        onChange={this.handlePageChange}
                        value={this.changePage}
                        placeholder={this.currentPage.toString()}
                    />

                    <button>Go</button>
                </form>

                <ul className={s.pages}>
                    {this.pagination.map(page => (
                        <li key={page}>
                            <button onClick={this.handlePageClick(page)}>
                                {((page === '<' || page === '>') && (
                                    <span>...</span>
                                )) ||
                                    (page === this.currentPage && (
                                        <strong>{page}</strong>
                                    )) || <em>{page}</em>}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    private handleGoToPage = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const next = parseInt(this.changePage, 10);
        if (!isNaN(next)) {
            this.props.onPageClick(
                next <= 0 ? 1 : next > this.pagesCount ? this.pagesCount : next
            );
        }

        this.changePage = '';
    };

    private handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.changePage = e.target.value;
    };

    private handlePageClick = (page: PaginationPage) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        let next: number = this.currentPage;

        if (page === '<') {
            next = this.currentPage - 1;
        } else if (page === '>') {
            next = this.currentPage + 1;
        } else {
            next = page;
        }

        this.props.onPageClick(
            next <= 0 ? 1 : next > this.pagesCount ? this.pagesCount : next
        );
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/navigation/Pagination.tsx